import React, { FC } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BODY_FONT, t } from '../../HomeScreen/HomeScreen.styles';
import { useTypedSelector } from '../../../../store/store';
import { selectUploadPhase, UploadPhase } from '../../../../services/upload/uploadPhase';

const Line = styled(Box)({
  fontSize: t(14.5),
  fontWeight: 500,
  fontFamily: BODY_FONT,
  color: '#4c5257',
  maxWidth: '40ch',
  // Reserve two lines: the telemetry string ("Part 256 KB • 1.1 Mbps • …")
  // can wrap on narrow screens, and a stable height keeps the step below
  // from jumping.
  minHeight: `calc(2 * 1.35 * ${t(14.5)})`,
  lineHeight: 1.35,
  textAlign: 'center',
  letterSpacing: 0,
});

const formatBytes = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 2 : 1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 2 : 1)} MB`;
};

const PHASES_WITH_METER: ReadonlySet<UploadPhase> = new Set([
  UploadPhase.Uploading,
  UploadPhase.UploadingSlow,
  UploadPhase.Dipped,
  UploadPhase.Resuming,
]);

/**
 * Live telemetry line under the progress ring. Surfaces what the user can
 * verify is happening: chunk size, smoothed throughput, bytes / total.
 *
 * Pulls from existing slices — no new state. Hidden outside active-upload
 * phases so it doesn't flash during transitions, and unconditionally hidden
 * once bytes are committed (the "Part 256 KB · 1.1 Mbps · …" line is stale
 * the moment uploading is done, regardless of whether the user has finished
 * answering questions or filling out identity).
 */
export const MeterLine: FC = () => {
  const phase = useTypedSelector(selectUploadPhase);
  const uploadingProgress = useTypedSelector((state) => state.upload.uploadingProgress);
  const { speedMbs, chunkSize } = useTypedSelector((state) => state.ui.uploadMetrics);
  const iterations = useTypedSelector((state) => state.upload.uploadingIterations);

  // Bytes done → meter is stale. This is the hard gate; the phase set above
  // is just a soft "we're actively uploading" filter.
  if (uploadingProgress >= 100) return null;
  if (!PHASES_WITH_METER.has(phase)) return null;

  const latest = iterations.length > 0 ? iterations[iterations.length - 1] : undefined;
  const uploaded = latest?.loaded ?? 0;
  const total = latest?.total ?? 0;

  const parts: string[] = [];
  if (chunkSize > 0) parts.push(`Part ${formatBytes(chunkSize)}`);
  if (speedMbs > 0) parts.push(`${speedMbs.toFixed(speedMbs < 10 ? 1 : 0)} Mbps`);
  if (total > 0) parts.push(`${formatBytes(uploaded)} / ${formatBytes(total)}`);

  return (
    <Line role="status" aria-live="polite">
      {parts.join(' • ')}
    </Line>
  );
};
